import React, { useState, useEffect } from 'react';
import { FaArrowUp } from 'react-icons/fa';
import styled, { useTheme } from 'styled-components';

// Styled Components
const BackToTopContainer = styled.div`
  position: fixed;
  bottom: 30px;
  right: 40px;
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  cursor: pointer;
  transition: opacity 0.3s ease-in-out;
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};

  @media (max-width: 768px) {
    bottom: 20px;
    right: 20px;
    width: 35px;
    height: 35px;
  }

  @media (max-width: 480px) {
    bottom: 15px;
    right: 15px;
    width: 30px;
    height: 30px;
  }
`;

const ProgressRing = styled.svg`
  position: absolute;
  top: 0;
  left: 0;
  width: 40px;
  height: 40px;

  @media (max-width: 768px) {
    width: 35px;
    height: 35px;
  }

  @media (max-width: 480px) {
    width: 30px;
    height: 30px;
  }
`;

const ProgressRingCircle = styled.circle`
  transition: stroke-dashoffset 0.35s;
  transform: rotate(-90deg);
  transform-origin: 50% 50%;
`;

const Icon = styled(FaArrowUp)`
  color: ${({ theme }) => theme.colors.secondary};
  font-size: 18px;
  z-index: 1;

  @media (max-width: 768px) {
    font-size: 16px;
  }

  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

const BackToTop = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const theme = useTheme();

  const toggleVisibility = () => {
    if (window.pageYOffset > 300) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  };

  const calculateScrollProgress = () => {
    const scrollTop = window.pageYOffset;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    const progress = (scrollTop / docHeight) * 100;
    setScrollProgress(progress);
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    const handleScroll = () => {
      toggleVisibility();
      calculateScrollProgress();
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const radius = 18;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (scrollProgress / 100) * circumference;

  return (
    <BackToTopContainer isVisible={isVisible} onClick={scrollToTop}>
      <ProgressRing width="40" height="40" viewBox="0 0 40 40">
        <ProgressRingCircle
          stroke={theme.colors.dark}
          strokeWidth="3"
          fill="transparent"
          r={radius}
          cx="20"
          cy="20"
          style={{ strokeDasharray: circumference, strokeDashoffset: 0 }}
        />
        <ProgressRingCircle
          stroke={theme.colors.secondary}
          strokeWidth="3"
          fill="transparent"
          r={radius}
          cx="20"
          cy="20"
          style={{ strokeDasharray: circumference, strokeDashoffset: offset }}
        />
      </ProgressRing>
      <Icon />
    </BackToTopContainer>
  );
};

export default BackToTop;
